import { NavLink, useLocation } from 'react-router-dom'
import type { HubBottomNavItem, HubMenuItem } from '../types'
import { clearAuth } from '../lib/auth'
import { LOGIN_PATH } from '../lib/hubPath'
import { menuIconGlyph } from '../lib/menuStyle'
import { pubImg } from '../lib/pubImg'
import { prefetchHubNavKey, prefetchHubSpaPath } from '../lib/routeChunks'

const FALLBACK_LOGO = pubImg('images/eleitorweb-300.png')
const FALLBACK_BRAND = pubImg('images/eleitorweb-300.png')

type Menus = {
  campanha: HubMenuItem[]
  rede: HubMenuItem[]
  perfil: HubMenuItem[]
}

type Props = {
  bottomNav: HubBottomNavItem[]
  menus?: Menus | null
  campanhaNome: string
  campanhaLogo: string
  brandNome?: string
  brandLogo?: string
  brandUrl?: string
}

function spaPath(href: string): string {
  const match = href.match(/\/hub(\/[^?#]*)/)
  return match ? `/hub${match[1]}` : href
}

function isMenuActive(pathname: string, item: HubMenuItem): boolean {
  if (!item.spa) return false
  const to = spaPath(item.href)
  if (!to || to === '/hub' || to === '/hub/') return false
  return pathname === to || pathname.startsWith(`${to}/`)
}

function MenuLink({ item }: { item: HubMenuItem }) {
  const location = useLocation()
  const icon = menuIconGlyph(item.icon)
  const label = (
    <>
      <span className="icon" aria-hidden>
        {icon}
      </span>
      {item.title}
    </>
  )

  if (item.spa) {
    const to = spaPath(item.href)
    return (
      <NavLink
        to={to}
        onPointerEnter={() => prefetchHubSpaPath(to)}
        onFocus={() => prefetchHubSpaPath(to)}
        className={() =>
          ['bp-side-nav__link', isMenuActive(location.pathname, item) ? 'active' : ''].filter(Boolean).join(' ')
        }
      >
        {label}
      </NavLink>
    )
  }

  return (
    <a href={item.href || '#'} className="bp-side-nav__link">
      {label}
    </a>
  )
}

export function SideNav({
  bottomNav,
  menus,
  campanhaNome,
  campanhaLogo,
  brandNome = 'EleitorWEB',
  brandLogo,
  brandUrl = 'https://eleitorweb.com.br',
}: Props) {
  const location = useLocation()
  const logoSrc = campanhaLogo?.trim() || FALLBACK_LOGO
  const brandSrc = brandLogo?.trim() || FALLBACK_BRAND
  const convidar = bottomNav.find((i) => i.key === 'convidar')
  const campanhaItems = menus?.campanha ?? []
  const redeItems = menus?.rede ?? []
  const perfilItems = (menus?.perfil ?? []).filter((i) => i.id !== 'perfil')

  return (
    <aside className="bp-side-nav" aria-label="Navegação principal">
      <div className="bp-side-nav__brand">
        <img
          src={logoSrc}
          alt={campanhaNome || 'Campanha'}
          className="bp-side-nav__logo"
          onError={(e) => {
            if (e.currentTarget.src !== FALLBACK_LOGO) {
              e.currentTarget.src = FALLBACK_LOGO
            }
          }}
        />
        {campanhaNome ? <p className="bp-side-nav__campanha">{campanhaNome}</p> : null}
      </div>

      <nav className="bp-side-nav__list">
        <NavLink
          to="/hub/inicio"
          className={({ isActive }) =>
            ['bp-side-nav__link', isActive || location.pathname === '/' ? 'active' : ''].filter(Boolean).join(' ')
          }
        >
          <span className="icon" aria-hidden>
            ⌂
          </span>
          Início
        </NavLink>

        {convidar?.spa ? (
          <NavLink to="/hub/convidar" className={({ isActive }) => ['bp-side-nav__link', 'cta', isActive ? 'active' : ''].join(' ')}>
            <span className="icon" aria-hidden>
              +
            </span>
            Convidar
          </NavLink>
        ) : null}

        {campanhaItems.length > 0 ? (
          <div className="bp-side-nav__section">
            <p className="bp-side-nav__section-title">{campanhaNome || 'Campanha'}</p>
            {campanhaItems.map((item) => (
              <MenuLink key={item.id || item.href} item={item} />
            ))}
          </div>
        ) : null}

        {redeItems.length > 0 || perfilItems.length > 0 ? (
          <div className="bp-side-nav__section">
            <p className="bp-side-nav__section-title">Liderança</p>
            <NavLink
              to="/hub/perfil"
              onPointerEnter={() => prefetchHubNavKey('perfil')}
              onFocus={() => prefetchHubNavKey('perfil')}
              className={({ isActive }) =>
                [
                  'bp-side-nav__link',
                  isActive || ['/cadastro', '/senha', '/ficha'].some((p) => location.pathname.startsWith(p))
                    ? 'active'
                    : '',
                ]
                  .filter(Boolean)
                  .join(' ')
              }
            >
              <span className="icon" aria-hidden>
                👤
              </span>
              Meu perfil
            </NavLink>
            {redeItems.map((item) => (
              <MenuLink key={item.id || item.href} item={item} />
            ))}
            {perfilItems.map((item) => (
              <MenuLink key={item.id || item.href} item={item} />
            ))}
          </div>
        ) : null}

        <div className="bp-side-nav__section">
          <p className="bp-side-nav__section-title">Conta</p>
          <NavLink
            to="/hub/senha"
            className={({ isActive }) => ['bp-side-nav__link', isActive ? 'active' : ''].filter(Boolean).join(' ')}
          >
            <span className="icon" aria-hidden>
              🔒
            </span>
            Trocar senha
          </NavLink>
          <button
            type="button"
            className="bp-side-nav__link bp-side-nav__link--button"
            onClick={() => {
              clearAuth()
              window.location.href = LOGIN_PATH
            }}
          >
            <span className="icon" aria-hidden>
              ↩
            </span>
            Sair
          </button>
        </div>
      </nav>

      <div className="bp-side-nav__footer">
        <a
          href={brandUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="bp-side-nav__system"
          title={brandNome}
        >
          <img
            src={brandSrc}
            alt={brandNome}
            className="bp-side-nav__system-logo"
            onError={(e) => {
              if (e.currentTarget.src !== FALLBACK_BRAND) {
                e.currentTarget.src = FALLBACK_BRAND
              }
            }}
          />
        </a>
      </div>
    </aside>
  )
}
